<template>
  <!-- Main Conversation Window -->
  <main class="flex-1 flex flex-col p-4 bg-gray-50 overflow-hidden h-[calc(100vh-4rem)]">
    <div class="border-b pb-2 text-lg font-semibold flex items-center justify-between">
      <div class="flex items-center">
        <button class="lg:hidden bg-paletteOrange p-2 mr-2 rounded" @click="goBack">
          <svg class="w-6 h-6 text-paletteOrange dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M5 12h14M5 12l4-4m-4 4 4 4" />
          </svg>
        </button>
        <span>{{ titleConversation }}</span>
      </div>
      <div class="flex items-center space-x-2">
        <img src="@/assets/icons/coin.png" alt="Credits Icon" class="w-6 h-6" />
        <RollingNumber :number="creditsLeft" />
      </div>
    </div>

    <!-- Messages List -->
    <div ref="messagesContainer" class="flex-1 flex flex-col overflow-y-auto p-1">
      <ConversationBubble v-for="(message, index) in messagesStore.messages" :key="index" :message="message.content"
        :isUser="message.role === 'user'" :messageId="message.message_id" @select="selectMessage" />

      <!-- AI is typing animation -->
      <div v-if="messagesStore.loading" class="flex items-center space-x-1 p-2">
        <span class="text-gray-500">{{ $t('conversationWindow.aiWriting') }}</span>
        <div class="dot-flashing"></div>
      </div>
    </div>

    <!-- Message Input -->
    <div class="mt-4 flex">
      <input v-model="newMessageText" type="text" :placeholder="$t('conversationWindow.messagePlaceholder')"
        class="w-full p-2 border border-paletteDarkGreen rounded-md" maxlength="600" @keyup.enter="sendMessage"
        :disabled="messagesStore.loading" />
      <button @click="sendMessage" class="ml-2 p-2 bg-paletteDarkGreen text-white rounded-md hover:bg-opacity-90"
        :disabled="messagesStore.loading">
        {{ $t('conversationWindow.sendButton') }}
      </button>
    </div>

    <!-- Error Modal -->
    <div v-if="messagesStore.hasError"
         class="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50"
         @click.self="closeErrorModal">
      <div class="bg-white rounded-lg p-8 max-w-md w-full mx-4 relative">
        <button @click="closeErrorModal"
                class="absolute top-4 right-4 text-gray-500 hover:text-gray-700">
          <span class="text-xl">×</span>
        </button>
        <div class="flex flex-col items-center text-center">
          <img src="@/assets/PapoteAI_Error.png" alt="Error" class="w-24 h-24 mb-4">
          <h3 class="text-xl font-bold text-red-600 mb-2">{{ $t('conversationWindow.errorTitle') }}</h3>
          <p class="text-gray-700 mb-4">
            {{ $t(getErrorMessageKey()) }}
          </p>
          <div class="flex gap-4">
            <button v-if="messagesStore.errorType === 'fetch'"
                    @click="retryFetch"
                    class="px-4 py-2 bg-paletteOrange text-white rounded-md hover:bg-opacity-90">
              {{ $t('conversationWindow.tryAgain') }}
            </button>
            <button @click="closeErrorModal"
                    class="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
              {{ $t('conversationWindow.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
@keyframes dotFlashing {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }

  100% {
    opacity: 1;
  }
}

.dot-flashing {
  width: 8px;
  height: 8px;
  background-color: gray;
  border-radius: 50%;
  animation: dotFlashing 1.5s infinite ease-in-out;
}
</style>

<script setup>
import { ref, watch, nextTick, computed } from 'vue'
import { useMessagesStore } from '@/stores/messages'
import { useNavigationStore } from '@/stores/navigation'
import { useI18n } from 'vue-i18n'
import ConversationBubble from '@/components/conversations/ConversationBubble.vue'
import { useUserStore } from '@/stores/users'
import { creditsLeftFormatted } from '@/utils/userUtils'
import RollingNumber from '@/components/utils/RollingNumber.vue'

const emit = defineEmits(['back', 'select-message'])
const { t } = useI18n({ useScope: 'global' })
const navigationStore = useNavigationStore()
const messagesStore = useMessagesStore()
const userStore = useUserStore()
const newMessageText = ref('')
const messagesContainer = ref(null)
const titleConversation = computed(() => {
  if (messagesStore.currentConversation) {
    return t('conversationWindow.currentConversationScenario', {
      scenario: messagesStore.currentConversation.scenario
    })
  } else {
    return t('conversationWindow.selectConversation')
  }
})
const creditsLeft = computed(() => creditsLeftFormatted(userStore.billingCreditsLeft))

// Watch for new messages and scroll to bottom
watch(() => messagesStore.messages.length, async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
  lastFailedMessage.value = ''
})

// Handle message selection for insights
function selectMessage (messageId, message, isUser) {
  emit('select-message', { messageId, message, isUser })
}

function getErrorMessageKey () {
  switch (messagesStore.errorType) {
    case 'fetch':
      return 'conversationWindow.fetchError'
    case 'send':
      return 'conversationWindow.sendError'
    case 'credits':
      return 'conversationWindow.creditsError'
    default:
      return 'conversationWindow.sendError'
  }
}

async function retryFetch () {
  if (messagesStore.currentConversation) {
    await messagesStore.fetchMessages(messagesStore.currentConversation.conversation_id)
  }
}

const lastFailedMessage = ref('')

function closeErrorModal () {
  console.log('Closing error modal')
  messagesStore.hasError = false
  messagesStore.errorType = null
  messagesStore.errorMessage = null
  // Restore the failed message to the input if it exists
  if (lastFailedMessage.value) {
    console.log('Restoring last failed message:', lastFailedMessage.value)
    newMessageText.value = lastFailedMessage.value
    lastFailedMessage.value = ''
  }
}

async function sendMessage () {
  const messageText = newMessageText.value.trim()
  if (messageText !== '' && !messagesStore.loading) {
    // Store the message in case we need to restore it
    lastFailedMessage.value = messageText
    // Clear the input immediately for better UX
    newMessageText.value = ''

    try {
      await messagesStore.postMessage({
        message_id: `message${messagesStore.messages.length + 1}`,
        content: messageText,
        role: 'user'
      })
    } catch (error) {
      // Error is handled by the store, message will be restored when modal is closed
      console.error('Failed to send message:', error)
    }
  }
}

function goBack () {
  messagesStore.currentConversation = null
  setTimeout(() => {
    navigationStore.setActiveSection('sidebar')
  }, 50)
}
</script>
